import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Badge, Button, Group, Menu, SegmentedControl, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, costsApi, type CostPlan, type CostStage, type CostValue, type CostValueInput, type CostValueKind, undoPaths } from '../../api';
import { COST_STAGES, COST_VALUE_KINDS } from '../../format';
import { EditableText } from '../../ui/EditableText';
import { FormulaCell } from '../../ui/FormulaCell';
import { Section } from '../../ui/Section';
import { useContextMenu } from '../../ui/ContextMenu';
import { kgFunctions, resultFor, variablesOf } from './variant';
import { guardedStep, inversePatch, useUndo } from '../../ui/undo';

const SUGGESTED: Record<CostValueKind, { name: string; label: string; unit: string; formula?: string }[]> = {
  quantity: [
    { name: 'BGF', label: 'Brutto-Grundfläche', unit: 'm²' },
    { name: 'BRI', label: 'Brutto-Rauminhalt', unit: 'm³' },
    { name: 'NUF', label: 'Nutzungsfläche', unit: 'm²' },
    { name: 'GRF', label: 'Grundstücksfläche', unit: 'm²' },
  ],
  rate: [],
  ratio: [
    { name: 'KZ_BGF', label: 'Gesamtkosten je m² BGF', unit: '€/m²', formula: 'GESAMT/BGF' },
    { name: 'KZ_BWK', label: 'Bauwerkskosten (KG 300+400) je m² BGF', unit: '€/m²', formula: '(KG(300)+KG(400))/BGF' },
  ],
};

/**
 * The plan's named values in three kinds: quantities, rates and ratios. With
 * variants there is one column per variant; a cell shows the variant's own
 * formula in full colour and the shared one greyed, so what differs stands
 * out at a glance. Typing into a variant cell gives it its own formula;
 * emptying it returns to the shared one.
 */
export function ValuesPanel({ projectId, plan, stage, canEdit }: { projectId: string; plan: CostPlan; stage: CostStage; canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['costs', projectId] });
  const [kind, setKind] = useState<CostValueKind>('quantity');
  const undo = useUndo();
  const save = useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & CostValueInput) => {
      if (!id) return costsApi.addValue(projectId, { ...body, name: body.name ?? '', formula: body.formula ?? '' }).then(() => undefined);
      const before = plan.values.find((v) => v.id === id);
      let step = null;
      if (before) {
        const { perVariant, ...rest } = body;
        const back: CostValueInput = inversePatch(rest, before as unknown as Record<string, unknown>);
        if (perVariant) back.perVariant = Object.fromEntries(Object.keys(perVariant).map((vid) => [vid, before.perVariant[vid] ?? null]));
        step = guardedStep(`Wert ${before.name}`, 'PATCH', undoPaths.costValue(projectId, id), back, body);
      }
      return undo.recordAfter(costsApi.updateValue(projectId, id, body), step);
    },
    onSettled: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => costsApi.removeValue(projectId, id), onSettled: refresh });
  const [draft, setDraft] = useState({ name: '', label: '', formula: '', unit: '' });
  const error = save.error ?? remove.error;
  // Right-click on a value: remove it, or drop a variant's own figures (the shared one applies again).
  const valueMenu = useContextMenu<CostValue>((v, close) => {
    const run = (f: () => void) => () => { close(); f(); };
    const own = Object.keys(v.perVariant ?? {});
    return (
      <>
        <Menu.Label>{v.name}</Menu.Label>
        {own.length > 0 && (
          <Menu.Item onClick={run(() => save.mutate({ id: v.id, perVariant: Object.fromEntries(own.map((k) => [k, null])) }))}>
            eigene Werte der Varianten löschen ({own.length})
          </Menu.Item>
        )}
        <Menu.Item color="red" onClick={run(() => { if (window.confirm(`${v.name} löschen? Formeln, die ${v.name} verwenden, melden danach einen Fehler.`)) remove.mutate(v.id); })}>Wert löschen …</Menu.Item>
      </>
    );
  });

  const values = plan.values.filter((v) => v.kind === kind);
  const variants = plan.variants;
  const results = variants.length > 0 ? variants.map((v) => resultFor(plan, v.id)) : [resultFor(plan, '')];
  const valueOf = (v: CostValue, i: number): { value: number | null; error: string | null } | undefined =>
    v.kind === 'ratio' ? results[i]!.ratios[stage]?.[v.name] : results[i]!.values[v.name];
  const missing = SUGGESTED[kind].filter((s) => !plan.values.some((q) => q.name.toLowerCase() === s.name.toLowerCase()));
  const counts = Object.fromEntries((Object.keys(COST_VALUE_KINDS) as CostValueKind[]).map((k) => [k, plan.values.filter((v) => v.kind === k).length]));
  const unitPlaceholder = kind === 'quantity' ? 'm²' : '€/m²';

  return (
    <Section
      title="Mengen, Kennwerte und Kennzahlen"
      description={
        <>
          In Formeln mit ihrem Namen verwendbar, z. B. <code>A_BEF*KW_530</code>. Die Reihenfolge ist frei; jeder Wert darf andere verwenden.
          {variants.length > 0 && ' Je Variante: kräftig = eigener Ansatz dieser Variante, grau = gemeinsamer Ansatz.'}
        </>
      }
    >
      {error && <Alert color="red" py={6} withCloseButton onClose={() => { save.reset(); remove.reset(); }}>{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>}
      <SegmentedControl
        size="xs"
        value={kind}
        onChange={(v) => setKind(v as CostValueKind)}
        data={(Object.keys(COST_VALUE_KINDS) as CostValueKind[]).map((k) => ({ value: k, label: `${COST_VALUE_KINDS[k].label}${counts[k] ? ` (${counts[k]})` : ''}` }))}
      />
      <Text size="xs" c="dimmed">{COST_VALUE_KINDS[kind].hint}.</Text>
      {values.length > 0 && (
        <Table.ScrollContainer minWidth={560 + variants.length * 130}>
          <Table verticalSpacing={3} className="pst-tabular" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                {canEdit && <Table.Th w={30} />}
                <Table.Th w={150}>Name</Table.Th>
                <Table.Th>Bezeichnung</Table.Th>
                <Table.Th w={variants.length > 0 ? 170 : 220}>{variants.length > 0 ? 'Ansatz (gemeinsam)' : 'Ansatz'}</Table.Th>
                {variants.length > 0
                  ? variants.map((v) => <Table.Th key={v.id} w={130} ta="right" title={v.name}>{v.code}</Table.Th>)
                  : <Table.Th w={130} ta="right">Wert</Table.Th>}
                <Table.Th w={70}>Einheit</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {values.map((v) => (
                <Table.Tr key={v.id} onContextMenu={canEdit ? (e) => valueMenu.open(e, v) : undefined}>
                  {canEdit && <Table.Td><ActionIcon size="xs" variant="subtle" color="gray" aria-label={`${v.name} löschen`} title="Wert löschen" onClick={() => remove.mutate(v.id)}>✕</ActionIcon></Table.Td>}
                  <Table.Td>
                    <Tooltip label={v.note} disabled={!v.note} multiline w={360} position="right">
                      <div><EditableText value={v.name} monospace bold onCommit={(name) => canEdit && save.mutate({ id: v.id, name })} /></div>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <EditableText value={v.label ?? ''} allowEmpty placeholder=" " onCommit={(label) => canEdit && save.mutate({ id: v.id, label: label || null })} />
                  </Table.Td>
                  <Table.Td><EditableText value={v.formula} monospace onCommit={(formula) => canEdit && save.mutate({ id: v.id, formula })} /></Table.Td>
                  {results.map((r, i) => {
                    const variant = variants[i];
                    const own = variant ? v.perVariant[variant.id] : undefined;
                    const res = valueOf(v, i);
                    return (
                      <Table.Td key={r.variantId || 'all'} style={own !== undefined ? { background: 'var(--mantine-color-brand-light)' } : undefined}>
                        <div style={{ opacity: variant && own === undefined ? 0.6 : 1 }}>
                          <FormulaCell
                            formula={own ?? v.formula}
                            value={res?.value ?? null}
                            error={res?.error}
                            variables={variablesOf(r, stage)}
                            functions={kgFunctions(r, stage)}
                            empty="—"
                            emptyHint={variant ? `leer = gemeinsamer Ansatz ${v.formula}` : 'Wert oder Formel'}
                            placeholder={v.formula}
                            readOnly={!canEdit || !variant}
                            onCommit={(next) => {
                              if (!variant) return;
                              const same = next === null || next.trim() === v.formula;
                              save.mutate({ id: v.id, perVariant: { [variant.id]: same ? null : next } });
                            }}
                          />
                        </div>
                      </Table.Td>
                    );
                  })}
                  <Table.Td><EditableText value={v.unit ?? ''} allowEmpty placeholder=" " onCommit={(unit) => canEdit && save.mutate({ id: v.id, unit: unit || null })} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {values.length === 0 && <Text size="sm" c="dimmed">Noch keine {COST_VALUE_KINDS[kind].label}.</Text>}
      {valueMenu.menu}
      {canEdit && (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!draft.name.trim() || !draft.formula.trim()) return;
              save.mutate(
                { name: draft.name.trim(), label: draft.label.trim() || null, kind, formula: draft.formula.trim(), unit: draft.unit.trim() || null },
                { onSuccess: () => setDraft({ name: '', label: '', formula: '', unit: '' }) },
              );
            }}
          >
            <Group gap="xs" align="flex-end" wrap="wrap">
              <TextInput size="xs" label="Name" placeholder={kind === 'rate' ? 'KW_330' : kind === 'ratio' ? 'KZ_BGF' : 'BGF'} value={draft.name} onChange={(e) => { const name = e.currentTarget.value; setDraft((d) => ({ ...d, name })); }} w={140} styles={{ input: { fontFamily: 'monospace' } }} />
              <TextInput size="xs" label="Bezeichnung" value={draft.label} onChange={(e) => { const label = e.currentTarget.value; setDraft((d) => ({ ...d, label })); }} w={240} />
              <TextInput size="xs" label="Wert oder Formel" placeholder={kind === 'ratio' ? 'GESAMT/BGF' : kind === 'rate' ? 'z. B. 450 oder 5%' : 'z. B. 24*12*2'} value={draft.formula} onChange={(e) => { const formula = e.currentTarget.value; setDraft((d) => ({ ...d, formula })); }} w={180} styles={{ input: { fontFamily: 'monospace' } }} />
              <TextInput size="xs" label="Einheit" placeholder={unitPlaceholder} value={draft.unit} onChange={(e) => { const unit = e.currentTarget.value; setDraft((d) => ({ ...d, unit })); }} w={80} />
              <Button size="xs" type="submit" variant="light" disabled={!draft.name.trim() || !draft.formula.trim()} loading={save.isPending}>{COST_VALUE_KINDS[kind].one} hinzufügen</Button>
            </Group>
          </form>
          {missing.length > 0 && (
            <Group gap={6}>
              <Text size="xs" c="dimmed">Übliche:</Text>
              {missing.map((s) => (
                <Tooltip key={s.name} label={s.label}>
                  <Button size="compact-xs" variant="subtle" onClick={() => setDraft({ name: s.name, label: s.label, formula: s.formula ?? '', unit: s.unit })}>{s.name}</Button>
                </Tooltip>
              ))}
            </Group>
          )}
        </>
      )}
      {kind === 'ratio' && (
        <Group gap={6}>
          <Badge size="xs" variant="light" color="gray">GESAMT</Badge><Text size="xs" c="dimmed">Summe mit Risiko</Text>
          <Badge size="xs" variant="light" color="gray">NETTO</Badge><Text size="xs" c="dimmed">Summe ohne Risiko</Text>
          <Badge size="xs" variant="light" color="gray">KG(300)</Badge><Text size="xs" c="dimmed">Summe einer Kostengruppe, KG(200;600) eines Bereichs</Text>
          <Text size="xs" c="dimmed">– gezeigt für die {COST_STAGES.find((s) => s.key === stage)!.label}.</Text>
        </Group>
      )}
    </Section>
  );
}
